// src/components/layout/Header/LanguageSwitcher.tsx

import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import i18n from '@/i18n'
import { getIcon } from '@/components/icons/MenuIcons'
import useClickOutside from '@/hooks/useClickOutside'

type LanguageOption = {
    code: string;
    name: string;
    flag: string;
}

const languages: LanguageOption[] = [
    {
        code: 'vi',
        name: 'Tiếng Việt',
        flag: '🇻🇳',
    },
    {
        code: 'en',
        name: 'English',
        flag: '🇺🇸',
    },
]

type LanguageSwitcherProps = {
    variant?: 'icon' | 'text' | 'full';
    size?: 'sm' | 'md' | 'lg';
    className?: string;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
    variant = 'full',
    size = 'md',
    className = '',
}) => {
    const { t } = useTranslation('common');
    const [showDropdown, setShowDropdown] = useState(false);
    
    // Sử dụng custom hook
    const [dropdownRef, handleClickOutside] = useClickOutside<HTMLDivElement>(() => {
        setShowDropdown(false)
    })
    
    // Kích hoạt click outside handling
    handleClickOutside(showDropdown)

    const handleLanguageChange = async (languageCode: string) => {
        try {
            await i18n.changeLanguage(languageCode)
            setShowDropdown(false)
        } catch (error) {
            console.error('Error changing language:', error)
        }
    }

    const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0]

    // Tailwind classes for button size
    const sizeClasses = {
        sm: 'p-1.5 text-xs',
        md: 'p-2 text-sm',
        lg: 'p-3 text-base',
    }[size];

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            <button
                type="button"
                className={`flex items-center gap-2 bg-gray-50 hover:bg-gray-100 rounded-md ${sizeClasses}`}
                onClick={() => setShowDropdown(!showDropdown)}
                aria-haspopup="true"
                aria-expanded={showDropdown}
            >
                {variant !== 'icon' && currentLanguage.flag}
                {variant === 'full' && (
                    <span className="hidden md:inline">{currentLanguage.name}</span>
                )}
                {variant === 'icon' && getIcon('Language', { className: 'h-5 w-5' })}
            </button>

            {showDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg overflow-hidden z-20 border border-gray-200">
                    <div className="py-1">
                        {languages.map((language) => (
                            <button
                                key={language.code}
                                className={`flex items-center w-full px-4 py-2 text-sm ${language.code === i18n.language ? 'bg-gray-100 text-indigo-600 font-medium' : 'text-gray-700 hover:bg-gray-50'}`}
                                onClick={() => handleLanguageChange(language.code)}
                            >
                                <span className="mr-2">{language.flag}</span>
                                <span>{t(`locales.${language.code}`)}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}

export default LanguageSwitcher;